import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Lock, User, UserPlus, Users, CreditCard, CheckCircle, Tag, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config/api';

const PROMO_CODE = 'BEELITE';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    role: 'epp_staff',
    promoCode: '',
    teamName: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Check if subscription is required based on role and promo code
  const requiresPayment = () => {
    // EPP Staff and Team Managers don't need to pay
    if (formData.role === 'coach' || formData.role === 'epp_staff' || formData.role === 'team_manager') return false;
    // Parents/Athletes with valid promo code don't need to pay
    if (formData.promoCode.toUpperCase() === PROMO_CODE) return false;
    // Everyone else needs to pay
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Team Manager requires team name
    if (formData.role === 'team_manager' && !formData.teamName.trim()) {
      toast.error('Please enter your team name');
      return;
    }
    
    setSubmitting(true);

    try {
      if (requiresPayment()) {
        // Store registration data temporarily before Stripe redirect
        const registrationData = {
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          role: formData.role,
          timestamp: Date.now()
        };
        localStorage.setItem('pendingRegistration', JSON.stringify(registrationData));
        
        // Create Stripe checkout session via API
        toast.info('Creating secure checkout session...');
        
        try {
          const response = await axios.post(`${API}/stripe/checkout/create`, {
            package_id: 'monthly',
            origin_url: window.location.origin,
            user_email: formData.email,
            metadata: {
              full_name: formData.fullName,
              role: formData.role
            }
          });
          
          if (response.data.checkout_url) {
            toast.success('Redirecting to secure payment...');
            // Redirect to Stripe Checkout
            window.location.href = response.data.checkout_url;
          } else {
            throw new Error('No checkout URL received');
          }
        } catch (stripeError) {
          console.error('Stripe checkout error:', stripeError);
          toast.error('Payment service temporarily unavailable. Please try again.');
          localStorage.removeItem('pendingRegistration');
          setSubmitting(false);
        }
      } else {
        // Direct registration without payment (EPP Staff, Team Managers, or valid promo code)
        const result = await register(formData.email, formData.password, formData.fullName, formData.role, formData.teamName);
        toast.success('Account created successfully!');
        
        // Redirect athletes and parents to onboarding first
        if (formData.role === 'athlete' || formData.role === 'parent') {
          navigate('/onboarding');
        } else if (formData.role === 'athlete' && result.child_id) {
          // After onboarding, athletes go to their profile
          navigate(`/child/${result.child_id}`);
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      setSubmitting(false);
    }
  };

  const showPromoField = formData.role === 'parent' || formData.role === 'athlete';
  const hasValidPromo = formData.promoCode.toUpperCase() === PROMO_CODE;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_youth-athlete-lab/artifacts/pr7l13yi_Elite%20%284%29.png" 
            alt="BE ELITE APP" 
            className="h-32 mx-auto mb-4"
          />
          <p className="text-gray-400 mt-2">Create your account</p>
        </div>

        {/* Subscription Info Banner - Only show for parent/athlete without valid promo */}
        {showPromoField && !hasValidPromo && (
          <div className="bg-gradient-to-r from-[#02b5ff]/10 to-[#00d4aa]/10 border border-[#02b5ff]/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <CreditCard className="h-5 w-5 text-[#02b5ff]" />
              <span className="font-semibold text-white">Subscription Required</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span><strong className="text-white">30 days FREE trial</strong> - no charge today</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span>Then only <strong className="text-white">£2.99/month</strong></span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span>Cancel anytime during trial</span>
              </div>
            </div>
          </div>
        )}

        {/* EPP Staff Free Access Banner */}
        {(formData.role === 'coach' || formData.role === 'epp_staff') && (
          <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span className="font-semibold text-white">Free EPP Staff Access</span>
            </div>
            <p className="text-sm text-gray-300 mt-2">
              EPP Staff get full access to the platform at no cost.
            </p>
          </div>
        )}

        {/* Team Manager Free Access Banner */}
        {formData.role === 'team_manager' && (
          <div className="bg-gradient-to-r from-purple-500/10 to-violet-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-purple-400" />
              <span className="font-semibold text-white">Free Team Manager Access</span>
            </div>
            <p className="text-sm text-gray-300 mt-2">
              Team Managers get view-only access to the platform at no cost.
            </p>
          </div>
        )}

        {/* Valid Promo Code Banner */}
        {showPromoField && hasValidPromo && (
          <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span className="font-semibold text-white">Promo Code Applied!</span>
            </div>
            <p className="text-sm text-gray-300 mt-2">
              Your code gives you free access to the platform.
            </p>
          </div>
        )}

        {/* Register Form */}
        <div className="bg-zinc-900 rounded-2xl shadow-xl p-8 border border-zinc-800">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                Full Name
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  data-testid="register-name-input"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="h-12 pl-10 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                  placeholder="John Smith"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  data-testid="register-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-12 pl-10 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                  placeholder="user@example.com"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                I am a...
              </Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500 z-10" />
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value, promoCode: '', teamName: '' })}
                >
                  <SelectTrigger data-testid="register-role-select" className="h-12 pl-10 bg-zinc-800 border-zinc-700 text-white rounded-xl">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 rounded-xl">
                    <SelectItem value="epp_staff" className="rounded-lg text-white">EPP Staff</SelectItem>
                    <SelectItem value="team_manager" className="rounded-lg text-white">Team Manager (View Only)</SelectItem>
                    <SelectItem value="parent" className="rounded-lg text-white">Parent</SelectItem>
                    <SelectItem value="athlete" className="rounded-lg text-white">Athlete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Team Name Field - Required for Team Manager */}
            {formData.role === 'team_manager' && (
              <div>
                <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                  Team Name <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <Input
                    data-testid="register-team-name-input"
                    type="text"
                    required
                    value={formData.teamName}
                    onChange={(e) => setFormData({ ...formData, teamName: e.target.value })}
                    className="h-12 pl-10 bg-zinc-800 border-zinc-700 focus:border-purple-500 focus:ring-purple-500 text-white rounded-xl"
                    placeholder="Enter your team name"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter the name of the team you manage.
                </p>
              </div>
            )}

            {/* Promo Code Field - Only for Parent/Athlete */}
            {showPromoField && (
              <div>
                <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                  Promo Code <span className="text-gray-500 font-normal">(optional)</span>
                </Label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <Input
                    data-testid="register-promo-input"
                    type="text"
                    value={formData.promoCode}
                    onChange={(e) => setFormData({ ...formData, promoCode: e.target.value })}
                    className={`h-12 pl-10 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl uppercase ${
                      hasValidPromo ? 'border-green-500 bg-green-500/10' : ''
                    }`}
                    placeholder="Enter promo code"
                  />
                  {hasValidPromo && (
                    <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-400" />
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Have a promo code? Enter it here for free access.
                </p>
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  data-testid="register-password-input"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-12 pl-10 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                  placeholder="••••••••"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  data-testid="register-confirm-password-input"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="h-12 pl-10 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button
              data-testid="register-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin mr-2">⏳</span>
                  {requiresPayment() ? 'Redirecting to payment...' : 'Creating account...'}
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  {requiresPayment() ? (
                    <>
                      <CreditCard className="mr-2 h-5 w-5" />
                      Continue to Payment
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2 h-5 w-5" />
                      Create Account
                    </>
                  )}
                </span>
              )}
            </Button>
            {requiresPayment() && (
              <p className="text-xs text-center text-gray-500 mt-3">
                You'll be redirected to Stripe for secure payment. Your 30-day free trial starts today.
              </p>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-[#02b5ff] hover:text-[#02b5ff] font-semibold"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-sm mt-6">
          © BE ELITE APP
        </p>
      </div>
    </div>
  );
}
